import { AlertCircle, UserX } from 'lucide-react';
import { useId, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ApiError } from '@/lib/errors';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  isPending: boolean;
  onConfirm: () => Promise<void> | void;
}

/**
 * Practitioner-side confirm for "Mark no-show". The client never joined, so
 * there is nothing to categorise: no concerns picker, no risk selector.
 *
 * We deliberately do NOT auto-detect whether the client joined. The reliable
 * signal lives server-side (the `room:joined:<sessionId>` Redis set the
 * auto-sweep reads) and isn't exposed on /call/status; the client-side
 * alternative is wrong after any LiveKit full reconnect, which unwinds every
 * remote participant. So the practitioner attests instead — the checkbox is
 * the gate, and Confirm stays disabled until it's ticked.
 *
 * Closing without confirming does nothing — the call stays live.
 */
export function MarkNoShowDialog({ open, onOpenChange, isPending, onConfirm }: Props) {
  const attestId = useId();
  const [attested, setAttested] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset transient state every time the dialog re-opens. Same "store info
  // from previous renders" pattern as LeaveAndCompleteDialog, which keeps
  // react-hooks/set-state-in-effect happy.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setAttested(false);
      setSubmitError(null);
    }
  }

  async function handleConfirm() {
    setSubmitError(null);
    try {
      await onConfirm();
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : 'Could not mark this session no-show.',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-title-md inline-flex items-center gap-2">
            <UserX className="text-accent-ink h-4 w-4" strokeWidth={1.75} aria-hidden />
            Mark as no-show?
          </DialogTitle>
          <DialogDescription className="text-body-sm leading-relaxed text-neutral-600">
            This ends the call for everyone and records the session as a no-show. It is not marked
            completed and carries no billable time.
          </DialogDescription>
        </DialogHeader>

        <label
          htmlFor={attestId}
          className="border-border/70 text-body-sm text-accent-ink mt-2 flex cursor-pointer items-start gap-2.5 rounded-md border bg-neutral-50/70 px-3 py-2.5"
        >
          <Checkbox
            id={attestId}
            checked={attested}
            onCheckedChange={(next) => {
              setAttested(next === true);
            }}
            className="mt-0.5"
          />
          <span className="flex-1 leading-relaxed">
            I confirm the client did not attend this session.
          </span>
        </label>

        {submitError ? (
          <div
            role="alert"
            className="border-error-300/60 bg-error-50/85 text-error-700 mt-2 flex items-start gap-2 rounded-md border px-3 py-2"
          >
            <AlertCircle className="mt-px size-4 shrink-0" strokeWidth={1.75} aria-hidden />
            <p className="text-body-sm flex-1">{submitError}</p>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="md"
            loading={isPending}
            disabled={!attested || isPending}
            onClick={() => {
              void handleConfirm();
            }}
          >
            {isPending ? 'Marking…' : 'Mark no-show'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
